import React, { useEffect, useRef } from "react";
import { Typography } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import { CompanyInfoFormStyled } from "./company-info-form.style";
import SelectFormField from "../../components/form-fields/select-field/select-form-field";
import { TextInputFormField } from "../..//components/form-fields/text-input-field/text-input-form-field";
import CheckBoxFormField from "../../components/form-fields/checkbox-field/check-box-form-field";
import {
  COMPANY_STATES_OPTIONS,
  COMPANY_TYPE_OPTIONS,
  COMPANY_PROVINCE_OPTIONS,
  COMPANY_US_STATE_OPTIONS,
  COMPANY_COUNTRY_OPTIONS,
} from "../../constants/constants";
import { useRateCard } from "../../hooks/useRateCards";

export const Company_INFO_FORM_VIEW_TYPES = {
  detail: "detail",
  creation: "creation",
};

const CompanyInfoForm = ({
  form,
  view = "creation",
  disableStateField = false,
}) => {
  const {
    data: rateCard = [],
    isLoading: rateCardsLoading,
    isError: rateCardsError,
  } = useRateCard();
  const { control, getValues, watch, setValue } = form;
  const {type} = watch();
  
  const formData = getValues();
  const isDetailedView = view === Company_INFO_FORM_VIEW_TYPES?.detail;
  const isCreationView = view === Company_INFO_FORM_VIEW_TYPES.creation;
  const { userPermissions } = useAuth();
  const isAdminUser = userPermissions === "Admin";

  // Watch the selected country field
  const selectedCountry = watch("country", "Canada"); // Default to Canada

  // Determine province options based on selected country
  const provinceOptions =
    selectedCountry === "US"
      ? COMPANY_US_STATE_OPTIONS
      : COMPANY_PROVINCE_OPTIONS;

  const isBillingRequired = !watch("isNotBilling", true);

  const initialNameRef = useRef(getValues("companyName"));
  // 2️⃣ on blur, check for a real change
  const handleNameBlur = () => {
    const current = getValues("companyName");
    if (current !== initialNameRef.current) {
      const ok = window.confirm(
        "Are you sure? Changing the company name will affect every instance in the system."
      );
      if (!ok) {
        // 3a️⃣ revert
        setValue("companyName", initialNameRef.current);
      } else {
        // 3b️⃣ accept and update ref
        initialNameRef.current = current;
      }
    }
  };

  // Only pre-fill the default rate card when creating a company. On the company
  // details page this effect must not run: Basic Info unmounts when switching
  // tabs, and remounting would otherwise overwrite the saved rate card with the
  // global default.
  useEffect(() => {
    if (!isCreationView || rateCardsLoading || rateCardsError) return;
    const def = rateCard.find((c) => c.isDefault);
    if (!def) return;
    const current = getValues("rateCard");
    if (current === undefined || current === null || current === "") {
      setValue("rateCard", def.id);
    }
  }, [
    isCreationView,
    rateCard,
    rateCardsLoading,
    rateCardsError,
    setValue,
    getValues,
  ]);

  const renderFieldsBasedOnView = () => {
    if (isCreationView) {
      return (
        <CompanyInfoFormStyled isDetailedView={isDetailedView}>
          <div className="creation-template">
            <div className="grid-3">
              <TextInputFormField
                isRequired
                label="Company Name"
                placeholder="Company Name"
                name={"companyName"}
                control={control}
                defaultValue={formData?.companyName}
              />
              <SelectFormField
                control={control}
                isRequired
                name="type"
                label="Type"
                placeholder="Select.."
                options={COMPANY_TYPE_OPTIONS}
              />
              <SelectFormField
                isRequired
                control={control}
                name="companyState"
                label="State"
                placeholder="Please Select"
                options={COMPANY_STATES_OPTIONS}
              />
            </div>
            <div className="flex">
              <SelectFormField
                isRequired
                control={control}
                name="rateCard"
                label="Company Rate Card"
                placeholder="Please Select"
                disabled={type === 'Vendor'}
                options={
                  rateCardsLoading || rateCardsError
                    ? []
                    : rateCard.map(({ id, name }) => ({
                        label: name,
                        value: id,
                      }))
                }
              />
              <TextInputFormField
                isRequired
                label="Phone Number"
                placeholder="1 (123) 456-7890"
                name={"phoneNumber"}
                control={control}
              />
              <TextInputFormField
                isRequired
                label="Web Address"
                placeholder="webaddress.com"
                name={"webAddress"}
                control={control}
              />
            </div>
            <hr className="hr"></hr>
            <div className="flex-block">
              <Typography variant="body4">Company Address</Typography>
            </div>
            <div className="flex">
              <TextInputFormField
                isRequired
                label="Address Line One"
                placeholder="Street Address"
                name="streetAddress"
                control={control}
              />
              <TextInputFormField
                label="Address Line Two"
                placeholder="Street Address"
                name="address"
                control={control}
              />
            </div>
            <div className="flex">
              <SelectFormField
                name="country"
                label="Country"
                control={control}
                isRequired
                placeholder="Select..."
                options={COMPANY_COUNTRY_OPTIONS}
                defaultValue="Canada"
              />
              <SelectFormField
                name="province"
                label="Province/State"
                control={control}
                isRequired
                placeholder="Select..."
                options={provinceOptions}
              />
              <TextInputFormField
                isRequired
                label="City"
                placeholder="Edmonton"
                name={"city"}
                control={control}
                defaultValue={formData?.city}
              />
              <TextInputFormField
                label="Postal/Zip"
                placeholder="A1B 2C3"
                name={"postalCode"}
                isRequired
                control={control}
              />
            </div>
            <hr className="hr"></hr>
            <div className="flex-block">
              <Typography variant="body4">Biling Address</Typography>
              <CheckBoxFormField
                isRequired
                control={control}
                name="isNotBilling"
                label="Same as company address"
                labelPlacement="end"
                size="small"
                // defaultValue={true}
              />
            </div>
            {!formData.isNotBilling && (
              <>
                <div className="flex">
                  <TextInputFormField
                    isRequired={isBillingRequired}
                    label="Address Line One"
                    placeholder="Street Address, P.0 box, etc"
                    name="billingStreetAddress"
                    control={control}
                  />
                  <TextInputFormField
                    label="Address Line Two"
                    placeholder="Apartment, Suite, Building, Floor"
                    name="billingAddress"
                    control={control}
                  />
                </div>
                <div className="flex">
                  <SelectFormField
                    name="billingCountry"
                    label="Country"
                    control={control}
                    isRequired={isBillingRequired}
                    placeholder="Select..."
                    options={COMPANY_COUNTRY_OPTIONS}
                    defaultValue="Canada"
                  />
                  <SelectFormField
                    name="billingProvince"
                    label="Province/State"
                    control={control}
                    isRequired={isBillingRequired}
                    placeholder="Select..."
                    options={provinceOptions}
                  />
                  <TextInputFormField
                    isRequired={isBillingRequired}
                    label="City"
                    placeholder="Edmonton"
                    name={"billingCity"}
                    control={control}
                  />
                  <TextInputFormField
                    label="Postal/Zip"
                    placeholder="A1B 2C3"
                    name={"billingPostalCode"}
                    isRequired={isBillingRequired}
                    control={control}
                  />
                </div>
              </>
            )}
            <hr className="hr"></hr>
            <div>
              <TextInputFormField
                multiline
                rows={5}
                label={"Company Notes"}
                placeholder={"Type your message..."}
                name={"companyNotes"}
                control={control}
                description={
                  "Notes are for internal use only and will not appear on outward-facing materials."
                }
              />
            </div>
          </div>
        </CompanyInfoFormStyled>
      );
    }

    if (isDetailedView) {
      return (
        <CompanyInfoFormStyled isDetailedView={isDetailedView}>
          <div className="detail-template">
            <div className="left-content">
              <TextInputFormField
                isRequired
                label="Company Name"
                name="companyName"
                control={control}
                defaultValue={formData?.companyName}
                disabled={!isAdminUser}
                onBlur={handleNameBlur}
              />
              <div className="flex">
                <SelectFormField
                  control={control}
                  isRequired
                  name="type"
                  label="Type"
                  placeholder="Select.."
                  options={COMPANY_TYPE_OPTIONS}
                />
                <SelectFormField
                  isRequired
                  control={control}
                  name="companyState"
                  label="State"
                  placeholder="Please Select"
                  options={COMPANY_STATES_OPTIONS}
                  disabled={disableStateField}
                />
                <SelectFormField
                  isRequired
                  control={control}
                  name="rateCard"
                  label="Company Rate Card"
                  placeholder="Please Select"
                  options={
                    rateCardsLoading || rateCardsError
                      ? []
                      : rateCard.map(({ id, name }) => ({
                          label: name,
                          value: id,
                        }))
                  }
                />
              </div>
              <div className="flex">
                <TextInputFormField
                  isRequired
                  label="Web Address"
                  name={"webAddress"}
                  control={control}
                />
                <TextInputFormField
                  isRequired
                  label="Phone Number"
                  placeholder="1 (123) 456-7890"
                  name={"phoneNumber"}
                  control={control}
                />
              </div>
              <hr className="hr"></hr>
              <div className="flex-block">
                <Typography variant="body4">Company Address</Typography>
              </div>
              <div className="flex">
                <TextInputFormField
                  isRequired
                  label="Address Line One"
                  placeholder="Street Address, P.0 box, etc"
                  name="streetAddress"
                  control={control}
                />
                <TextInputFormField
                  label="Address Line Two"
                  placeholder="Apartment, Suite, Building, Floor"
                  name="address"
                  control={control}
                />
              </div>
              <div className="flex">
                <SelectFormField
                  name="country"
                  label="Country"
                  control={control}
                  isRequired
                  placeholder="Select..."
                  options={COMPANY_COUNTRY_OPTIONS}
                  defaultValue="Canada"
                />
                <SelectFormField
                  name="province"
                  label="Province/State"
                  control={control}
                  isRequired
                  placeholder="Select..."
                  options={provinceOptions}
                />
                <TextInputFormField
                  isRequired
                  label="City"
                  placeholder="Edmonton"
                  name={"city"}
                  control={control}
                  defaultValue={formData?.city}
                />
                <TextInputFormField
                  label="Postal/Zip"
                  placeholder="A1B 2C3"
                  name={"postalCode"}
                  isRequired
                  control={control}
                />
              </div>
              <hr className="hr"></hr>
              <div className="flex-block">
                <Typography variant="body4">Biling Address</Typography>
                <CheckBoxFormField
                  control={control}
                  name="isNotBilling"
                  label="Same as company address"
                  labelPlacement="end"
                  size="small"
                  // defaultValue={true}
                />
              </div>
              {!formData.isNotBilling && (
                <>
                  <div className="flex">
                    <TextInputFormField
                      isRequired={isBillingRequired}
                      label="Address Line One"
                      placeholder="Street Address, P.0 box, etc"
                      name="billingStreetAddress"
                      control={control}
                    />
                    <TextInputFormField
                      label="Address Line Two"
                      placeholder="Apartment, Suite, Building, Floor"
                      name="billingAddress"
                      control={control}
                    />
                  </div>
                  <div className="flex">
                    <SelectFormField
                      name="billingCountry"
                      label="Country"
                      control={control}
                      isRequired={isBillingRequired}
                      placeholder="Select..."
                      options={COMPANY_COUNTRY_OPTIONS}
                      defaultValue="Canada"
                    />
                    <SelectFormField
                      name="billingProvince"
                      label="Province/State"
                      control={control}
                      isRequired={isBillingRequired}
                      placeholder="Select..."
                      options={provinceOptions}
                    />
                    <TextInputFormField
                      isRequired={isBillingRequired}
                      label="City"
                      placeholder="Edmonton"
                      name="billingCity"
                      control={control}
                    />
                    <TextInputFormField
                      label="Postal/Zip"
                      placeholder="A1B 2C3"
                      name={"billingPostalCode"}
                      isRequired={isBillingRequired}
                      control={control}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="notes ">
              <TextInputFormField
                multiline
                rows={21}
                sx={{ overflow: "hidden" }}
                label={"Company Notes"}
                name={"companyNotes"}
                control={control}
                description={
                  "Notes are for internal use only and will not appear on outward-facing materials."
                }
              />
            </div>
          </div>
        </CompanyInfoFormStyled>
      );
    }

    return null;
  };

  return (
    <CompanyInfoFormStyled>
      <div className="project-cont">
        <div className="row-style">{renderFieldsBasedOnView()}</div>
      </div>
    </CompanyInfoFormStyled>
  );
};

export default CompanyInfoForm;
